import { useId, useRef, useState, type FormEvent } from 'react';
import { ReviewDialog } from '../../payroll-review/components/ReviewDialog';

interface Props {
  busy: boolean;
  error: string | null;
  /** Called with the trimmed reason; never for a blank one. */
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

/** The preparer takes back a run they sent for approval, while it is still pending. It becomes Withdrawn (final, read-only, kept in the history) and the
 * approver sees that. A reason is required. Never possible after approval. */
export function WithdrawRunDialog({ busy, error, onConfirm, onCancel }: Props) {
  const reasonId = useId();
  const errorId = useId();
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) {
      setProblem('Enter a reason for withdrawing this run.');
      reasonRef.current?.focus();
      return;
    }
    setProblem(null);
    onConfirm(trimmed);
  }

  return (
    <ReviewDialog
      title="Withdraw this run from approval?"
      busy={busy}
      onClose={onCancel}
      initialFocusRef={reasonRef}
      description={
        <>
          <p>
            The run is taken back before anyone approves it. It stays in the run history marked <strong>Withdrawn</strong>, with your reason, and the approver sees that.
            It can&apos;t be edited or sent again: start a new Custom Pay Run to try again. Nothing is paid.
          </p>
          <p className="muted">This is only possible while the run is waiting for approval. Once it is approved it can no longer be withdrawn.</p>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <label htmlFor={reasonId} className="payroll-review__label">
          Reason for withdrawing <span aria-hidden="true">*</span>
          <span className="payroll-review__sr-only"> (required)</span>
        </label>
        <textarea
          id={reasonId}
          ref={reasonRef}
          rows={3}
          maxLength={500}
          value={reason}
          required
          aria-required="true"
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? errorId : undefined}
          disabled={busy}
          onChange={(e) => {
            setReason(e.target.value);
            if (problem) setProblem(null);
          }}
        />
        {problem && (
          <p id={errorId} className="error" role="alert">
            {problem}
          </p>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="payroll-review__dialog-actions">
          <button type="button" disabled={busy} onClick={onCancel}>
            Keep it pending
          </button>
          <button type="submit" className="payroll-review__danger" disabled={busy}>
            {busy ? 'Withdrawing…' : 'Withdraw run'}
          </button>
        </div>
      </form>
    </ReviewDialog>
  );
}
